import { UserManager, WebStorageStateStore } from "oidc-client-ts";

/**
 * Keycloak login (OpenID Connect authorization code flow with PKCE).
 *
 * Staff and students live in separate realms, so the realm is chosen on the
 * sign-in page and remembered for the trip to Keycloak and back.
 *
 * The access token is mirrored into sessionStorage under "token" whenever it
 * changes, because the rest of the app reads it from there synchronously via
 * utils/auth.js. oidc-client-ts stays the source of truth: it holds the refresh
 * token and renews in the background.
 *
 * Everything here is inert unless REACT_APP_KEYCLOAK_URL is set at build time,
 * so a build without it behaves exactly as before.
 */

const KEYCLOAK_URL = (process.env.REACT_APP_KEYCLOAK_URL || "").replace(/\/$/, "");
const CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "hec-frontend";

export const REALMS = {
  staff: process.env.REACT_APP_KEYCLOAK_STAFF_REALM || "hec-staff",
  student: process.env.REACT_APP_KEYCLOAK_STUDENT_REALM || "hec-students",
};

const REALM_KEY = "authRealm";
const REDIRECT_PATH = "/auth/callback";

/** True when this build is configured to use Keycloak at all. */
export const isKeycloakEnabled = () => Boolean(KEYCLOAK_URL);

/** Which realm the current session came from ("staff" | "student" | null). */
export const getSessionRealm = () => {
  try {
    return sessionStorage.getItem(REALM_KEY) || localStorage.getItem(REALM_KEY);
  } catch {
    return null;
  }
};

const managers = {};

const managerFor = (realmKey) => {
  const realm = REALMS[realmKey];
  if (!KEYCLOAK_URL || !realm) {
    return null;
  }
  if (!managers[realmKey]) {
    managers[realmKey] = new UserManager({
      authority: `${KEYCLOAK_URL}/realms/${realm}`,
      client_id: CLIENT_ID,
      redirect_uri: `${window.location.origin}${REDIRECT_PATH}`,
      post_logout_redirect_uri: window.location.origin,
      response_type: "code",
      scope: "openid profile email",
      // Refresh before expiry so long sessions do not drop mid-form.
      automaticSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 60,
      // Survives a page reload; cleared on logout.
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      monitorSession: false,
    });
    managers[realmKey].events.addUserLoaded((user) => storeToken(user));
    managers[realmKey].events.addUserUnloaded(() => clearToken());
    managers[realmKey].events.addSilentRenewError((error) => {
      console.error("Keycloak silent renew failed:", error);
    });
  }
  return managers[realmKey];
};

/** The manager for the realm this session belongs to, if any. */
const currentManager = () => {
  const realmKey = getSessionRealm();
  return realmKey ? managerFor(realmKey) : null;
};

const storeToken = (user) => {
  try {
    if (user?.access_token) {
      sessionStorage.setItem("token", user.access_token);
    }
  } catch (error) {
    console.error("could not store access token:", error);
  }
};

const clearToken = () => {
  try {
    sessionStorage.removeItem("token");
  } catch {
    // ignore: storage may be unavailable
  }
};

/** Sends the browser to Keycloak's login page for the given realm. */
export const loginWith = async (realmKey) => {
  const manager = managerFor(realmKey);
  if (!manager) {
    throw new Error(`Keycloak is not configured for realm "${realmKey}"`);
  }
  try {
    sessionStorage.setItem(REALM_KEY, realmKey);
    localStorage.setItem(REALM_KEY, realmKey);
  } catch {
    // ignore: the callback falls back to trying each realm
  }
  await manager.signinRedirect();
};

/**
 * Finishes the login after Keycloak redirects back. Returns the user, or null
 * if this was not a callback we can complete.
 */
export const completeLogin = async () => {
  const realmKey = getSessionRealm();
  // The remembered realm first; another sign-in started meanwhile (another tab, the other
  // login button) may have overwritten it, so the other realm can still finish this one
  const candidates = [...new Set([realmKey, ...Object.keys(REALMS)].filter(Boolean))];
  let lastError = null;
  for (const candidate of candidates) {
    const manager = managerFor(candidate);
    if (!manager) {
      continue;
    }
    try {
      const user = await manager.signinRedirectCallback();
      storeToken(user);
      try {
        sessionStorage.setItem(REALM_KEY, candidate);
        localStorage.setItem(REALM_KEY, candidate);
      } catch {
        // ignore
      }
      return user;
    } catch (error) {
      lastError = error;
    }
  }
  if (lastError) {
    throw lastError;
  }
  return null;
};

// Logout and token renewal need Keycloak's discovery document; fetching it once up front saves a
// round trip to the remote server (~0.6s) when the user signs out. oidc-client-ts caches it only
// once a fetch has finished, so the fetch is shared here; a failed one may be tried again.
const metadataLoads = new WeakMap();

const preloadMetadata = (manager) => {
  if (!metadataLoads.has(manager)) {
    metadataLoads.set(
      manager,
      manager.metadataService.getMetadata().catch((error) => {
        metadataLoads.delete(manager);
        console.warn("could not load Keycloak metadata:", error);
      })
    );
  }
};

// The restore in progress: every API request waits on it, so requests sent together share one
// session check (and one silent renewal when the token has expired) instead of one each
let restoring = null;

/**
 * Restores an existing Keycloak session on page load and refreshes the mirrored
 * token if it has expired. Returns the user or null. Concurrent calls share one restore.
 */
export const restoreSession = () => {
  if (!restoring) {
    restoring = restore().finally(() => {
      restoring = null;
    });
  }
  return restoring;
};

const restore = async () => {
  const manager = currentManager();
  if (!manager) {
    return null;
  }
  let user = await manager.getUser();
  if (user && user.expired) {
    try {
      user = await manager.signinSilent();
    } catch (error) {
      console.warn("Keycloak session could not be renewed:", error);
      await manager.removeUser();
      clearToken();
      return null;
    }
  }
  if (user) {
    storeToken(user);
    preloadMetadata(manager);
  }
  return user;
};

/** Ends the Keycloak session as well as the local one. */
export const keycloakLogout = async () => {
  const manager = currentManager();
  clearToken();
  try {
    sessionStorage.removeItem(REALM_KEY);
    localStorage.removeItem(REALM_KEY);
  } catch {
    // ignore
  }
  if (!manager) {
    return false;
  }
  try {
    await manager.signoutRedirect();
    return true;
  } catch (error) {
    console.error("Keycloak logout failed:", error);
    await manager.removeUser();
    return false;
  }
};
